package fr.frux.feature.treelist

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fr.frux.core.common.error.AppError
import fr.frux.core.common.error.asAppError
import fr.frux.core.common.logging.Logger
import fr.frux.core.maps.Location
import fr.frux.shared.domain.model.HarvestWindow
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.domain.model.currentHarvestMonth
import fr.frux.shared.domain.repository.SpeciesRepository
import fr.frux.shared.domain.repository.TreeRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

/**
 * Périmètre dont la liste parle (#229) : le centre et le rayon que la carte montrait quand
 * « Voir tout » a été tapé — porté par la route, jamais une position démo.
 */
data class TreeListOrigin(
    val latitude: Double,
    val longitude: Double,
    val radiusKm: Double,
    /**
     * Position reelle de l'utilisateur quand la carte a passe la main, si elle etait connue
     * (#260). Le centre ne peut pas en tenir lieu : il suit la camera, que l'utilisateur a pu
     * trainer a l'autre bout de la ville.
     */
    val userLatitude: Double? = null,
    val userLongitude: Double? = null,
) {
    /**
     * Point depuis lequel les distances de la liste sont mesurees : la position reelle si elle
     * est connue, sinon le centre interrogé — meme regle que la carte.
     */
    val distanceReference: Location
        get() =
            if (userLatitude != null && userLongitude != null) {
                Location(userLatitude, userLongitude)
            } else {
                Location(latitude, longitude)
            }
}

/**
 * ViewModel for the Tree List screen ("Tous les arbres").
 *
 * Interroge le même endpoint de proximité que la carte (pas d'endpoint « tous les arbres »
 * dédié), sur le périmètre transmis par [origin] (#229).
 */
@OptIn(ExperimentalTime::class)
class TreeListViewModel(
    private val treeRepository: TreeRepository,
    private val speciesRepository: SpeciesRepository,
    private val origin: TreeListOrigin,
) : ViewModel() {
    private val _state = MutableStateFlow(TreeListState())
    val state: StateFlow<TreeListState> = _state.asStateFlow()

    /** Mois courant, substituable en test (même pattern que `MapViewModel.timeSource`). */
    internal var currentMonth: Int = currentHarvestMonth()

    /** Horloge, substituable en test (#420) — pilote la mention de passage humain de la vignette. */
    internal var now: Instant = Clock.System.now()

    /** Fenêtres de récolte par espèce, dérivées du référentiel une fois chargé. */
    private var speciesHarvests: Map<Long, List<HarvestWindow>> = emptyMap()

    /** Dernier lot brut reçu, remappé quand le référentiel arrive après les arbres. */
    private var rawTrees: List<Tree> = emptyList()

    init {
        loadSpecies()
        loadTrees()
    }

    fun loadTrees() {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }

            treeRepository
                .getNearbyTrees(
                    lat = origin.latitude,
                    lon = origin.longitude,
                    radius = origin.radiusKm,
                    limit = LIST_PAGE_SIZE,
                ).collect { result ->
                    result.fold(
                        onSuccess = { nearby ->
                            rawTrees = nearby.trees
                            _state.update {
                                it.copy(
                                    trees = mapItems(nearby.trees),
                                    totalAvailable = nearby.totalAvailable,
                                    radiusKm = origin.radiusKm,
                                    isLoading = false,
                                    error = null,
                                )
                            }
                        },
                        onFailure = { error ->
                            _state.update {
                                it.copy(
                                    isLoading = false,
                                    error = error.asAppError(),
                                )
                            }
                        },
                    )
                }
        }
    }

    /**
     * Un échec du référentiel espèces ne doit jamais casser l'écran : la liste s'affiche quand
     * même, simplement sans libellé de saison (statut = mapping direct du statut déclaré).
     */
    private fun loadSpecies() {
        viewModelScope.launch {
            speciesRepository.getSpecies().fold(
                onSuccess = { species ->
                    speciesHarvests = species.associate { it.id to it.harvests }
                    _state.update { it.copy(trees = mapItems(rawTrees)) }
                },
                onFailure = { Logger.d(TAG, "⚠️ Référentiel espèces indisponible : ${it.message}") },
            )
        }
    }

    private fun mapItems(trees: List<Tree>): List<TreeListItem> =
        trees.map {
            it.toTreeListItem(
                harvests = speciesHarvests[it.species.id].orEmpty(),
                currentMonth = currentMonth,
                reference = origin.distanceReference,
                now = now,
            )
        }

    companion object {
        private const val TAG = "TreeListViewModel"

        /**
         * Plafond du lot demandé : la liste veut être exhaustive sur son périmètre, contrairement
         * à la carte (20). [TreeListState.totalAvailable] dit si le lot reste tronqué.
         */
        private const val LIST_PAGE_SIZE = 100
    }
}

data class TreeListState(
    val trees: List<TreeListItem> = emptyList(),
    val isLoading: Boolean = false,
    /** Catégorie d'échec du chargement (#176) ; le libellé est résolu par l'écran. */
    val error: AppError? = null,
    /** Total côté serveur dans le périmètre (#155) — peut dépasser [trees].size (lot tronqué). */
    val totalAvailable: Int? = null,
    /** Rayon interrogé (km), pour dire à l'utilisateur de quoi la liste parle (#229). */
    val radiusKm: Double? = null,
)
